import Image from "next/image";
import Link from "next/link";
import aolIcon from '@/app/images/aol_icon.jpg';
import yahooIcon from '@/app/images/yahoo_icon.jpg';
import creditKarmaIcon from '@/app/images/creditkarma_icon.jpg';
import equifaxIcon from '@/app/images/equifax_icon.jpg';
import amazonIcon from '@/app/images/amazon_icon.png';
import taboolaIcon from '@/app/images/taboola_icon.jpg';
import clsx from "clsx";
import { avenirNextBold, avenirNextMedium, avenirNextRegular } from "@/styles/fonts";

export default function PlanDescription(props: {
    product: any,
    discountedPriceCents?: number, // Optional discounted price in cents
}) {
    const { product, discountedPriceCents } = props;

    const formatCurrencyNumber = (value: number) => {
        const hasCents = value % 1 !== 0;
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: hasCents ? 2 : 0,
            maximumFractionDigits: 2,
        }).format(value);
    };

    const parsePrice = (value?: string) => {
        if (!value) {
            return undefined;
        }

        const numericValue = Number.parseFloat(value.replace(/[^0-9.]/g, ""));
        return Number.isNaN(numericValue) ? undefined : numericValue;
    };

    const parsedProductPrice = parsePrice(product.price);
    const formattedProductPrice = parsedProductPrice !== undefined
        ? `$${formatCurrencyNumber(parsedProductPrice)}`
        : product.price?.replace(".00", "");

    const listItemClassName = clsx(
        "py-2",
        "px-4",
        "text-[#86939e]",
        avenirNextRegular.className,
    );

    const butFirstListItemClassName = clsx(
        listItemClassName,
        'before:content-[""]',
        'before:relative',
        'before:block',
        'before:w-[130px]',
        'before:h-[2px]',
        'before:bg-[#f7f7f7]',
        'before:mx-auto',
        'before:mb-[10px]',
    )

    return (

        <div>
            <div className={clsx(
                "rounded-[10px]",
                "bg-white",
                "shadow",
                "overflow-hidden",
                "mt-[10px]",
                "shadow-lg",
                "max-w-[400px]",
                "mx-[10px]",
                "sm:mx-auto",
            )}>
                <h3 className={clsx(
                    "text-center",
                    "text-[#44494e]",
                    "text-[13px]",
                    "bg-[#fafbfc]",
                    "py-[18px]",
                    "md:py-[26px]",
                    "px-[15px]",
                    "uppercase",
                    "border-b",
                    "border-[#e9eff4]",
                    avenirNextBold.className,
                )}>Selected plan</h3>
                <div className={clsx(
                    "text-center",
                    "px-[30px]",
                    "py-[20px]",
                    avenirNextRegular.className,
                    'text-[#86939e]',
                )} id="description">
                    {/* {{ above_plan_html | raw}} */}
                    <h6>
                        <span className={clsx(
                            "text-[#42474c]",
                            avenirNextMedium.className,
                            "text-[22px]",
                        )}>
                            {product.name}
                        </span>
                    </h6>
                    <h1 className="py-[10px]">
                        {discountedPriceCents !== undefined ? (
                            <>
                                <span className={clsx(
                                    "text-[#939fa9]",
                                    "text-[20px]",
                                    "font-[700]",
                                    "line-through",
                                    "mr-2",
                                )}>{formattedProductPrice}</span>
                                <span className={clsx(
                                    "text-[#1585d8]",
                                    "text-[30px]",
                                    "font-[700]",
                                )}>${formatCurrencyNumber(discountedPriceCents / 100)}</span>
                            </>
                        ) : (
                            <span className={clsx(
                                "text-[#1585d8]",
                                "text-[30px]",
                                "font-[700]",
                            )}>{formattedProductPrice}</span>
                        )}
                        <small className={clsx(
                            "text-[16px]",
                            avenirNextMedium.className,
                            "text-[#939fa9]",
                        )}>/{product.interval}</small>
                    </h1>
                    {/* {% if args.belowPriceHtml %} {{ args.belowPriceHtml | raw }} {% endif %} */}
                    {product.desc2.lower_plan &&
                        <p className={clsx(
                            "bg-[#1685d8]",
                            "text-white",
                            "text-[14px]",
                            avenirNextRegular.className,
                            "mx-auto",
                            "inline-block",
                            "py-[2px]",
                            "px-[10px]",
                        )}>{product.desc2.lower_plan}</p>
                    }
                    <ul className={clsx(
                        "text-[14px]",
                    )}>
                        {product.desc3.map((item: { text: string, tip: string }, index: number) => (
                            <li key={item.text} className={index > 0 ? butFirstListItemClassName : listItemClassName}>
                                {item.tip != "" ?
                                    <>
                                        <Link href="#" data-for={`tooltip-${index}`}>{item.text}</Link>
                                        {/* TODO: <Tooltip anchorSelect={`#description a[data-for="tooltip-${index}"]`}
                                                    id={`tooltip-${index}`}
                                                    style={{ ...tooltipStyle, color: "#86939e" }}
                                                    opacity={1}
                                                    place="top">
                                                    {item.tip}
                                                </Tooltip> */}
                                    </> :
                                    item.text
                                }
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <ul className={clsx(
                "text-[#86939e]",
                "text-[14px]",
                "flex",
                "flex-col",
                "items-center",
                "gap-[30px]",
            )}>
                <li className={clsx(
                    "pt-[52px]",
                    "text-center",
                )}>Trusted by the top advertisers,<br /> agencies, and publishers</li>
                <li><Image
                    width={71}
                    height={29}
                    src={aolIcon} alt="aoi" /></li>
                <li><Image
                    width={152}
                    height={29}
                    src={yahooIcon} alt="Yahoo" /></li>
                <li><Image
                    width={171}
                    height={25}
                    src={creditKarmaIcon} alt="creditkarma" /></li>
                <li><Image
                    width={141}
                    height={26}
                    src={equifaxIcon} alt="equifax" /></li>
                <li><Image
                    width={143}
                    height={29}
                    src={amazonIcon} alt="amazon1" /></li>
                <li><Image
                    width={126}
                    height={30}
                    src={taboolaIcon} alt="taboola" /></li>
            </ul>
        </div>
    );
}